import { useMemo } from 'react'
import { Collapse, Typography, Space } from 'antd'
import { FileText, ExternalLink } from 'lucide-react'
import type { ChatSource } from '@/types/api.types'
import { t } from '@/config/translations'
import styles from './SourcesList.module.css'

const { Text, Link } = Typography

export interface SourcesListProps {
  sources: ChatSource[]
}

function getSourceUrl(source: ChatSource): string | null {
  if (source.url) return source.url
  // Construct URLs for GitLab/GitHub
  if (source.file_path && source.source) {
    // This is a simplified example - adjust based on your backend metadata
    return null // Backend should provide full URLs
  }
  return null
}

function getSourceTitle(source: ChatSource): string {
  if (source.title) return source.title
  if (source.file_name) return source.file_name
  if (source.file_path) return source.file_path.split('/').pop() ?? source.file_path
  if (source.issue_key) return source.issue_key
  return t('source_document')
}

export function SourcesList({ sources }: SourcesListProps) {
  // Generate stable unique IDs for each source (only once when sources change)
  // Must be called before any conditional returns (Rules of Hooks)
  const sourceKeys = useMemo(
    () => sources.map((_, index) => `source-${String(index)}-${crypto.randomUUID()}`),
    [sources]
  )

  if (sources.length === 0) {
    return null
  }

  // Individual source items with collapsible quotes
  const sourceItems = sources.map((source, index) => {
    const url = getSourceUrl(source)
    const title = getSourceTitle(source)

    // Use pre-generated stable unique key
    const uniqueKey = sourceKeys[index]
    const sourceId = source.id ?? `source-${String(index + 1)}`

    return {
      key: uniqueKey,
      label: (
        <div data-source-id={sourceId}>
          <Space size={8}>
            <Text type="secondary" style={{ fontSize: '12px', minWidth: '24px' }}>
              [{String(index + 1)}]
            </Text>
            <FileText size={14} />
            {url ? (
              <Link
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => {
                  e.stopPropagation()
                }}
              >
                <Space size={4}>
                  <Text strong style={{ fontSize: '13px' }}>
                    {title}
                  </Text>
                  <ExternalLink size={12} />
                </Space>
              </Link>
            ) : (
              <Text strong style={{ fontSize: '13px' }}>
                {title}
              </Text>
            )}
          </Space>
        </div>
      ),
      children: source.content ? (
        <div className={styles['sourceContent']}>{source.content}</div>
      ) : (
        <Text type="secondary" style={{ fontSize: '12px' }}>
          Нет цитаты
        </Text>
      ),
    }
  })

  const items = [
    {
      key: 'sources',
      label: (
        <Space size={8}>
          <FileText size={16} />
          <Text strong>{t('sources', { count: String(sources.length) })}</Text>
        </Space>
      ),
      children: (
        <Collapse size="small" items={sourceItems} defaultActiveKey={[]} style={{ marginTop: 8 }} />
      ),
    },
  ]

  const collapseClassName = styles['sourcesCollapse']
  const collapseProps = collapseClassName ? { className: collapseClassName } : {}

  return (
    <div style={{ marginTop: '12px' }}>
      <Collapse size="small" items={items} defaultActiveKey={[]} {...collapseProps} />
    </div>
  )
}
